// ─── src/components/gewFeedback/CatalystFeedbackPanel.tsx ────────────────────
//
// GEW feedback for one of the catalyst's meetups: a button that emails the
// form to attendees, then the charts and every answer with the name.

import { useState, type FC } from "react";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, MessageSquare } from "lucide-react";
import {
  getCatalystEventFeedback,
  sendCatalystFeedback,
} from "../../api/gewFeedback";
import { FeedbackCharts, FeedbackKpis, ResponsesList } from "./FeedbackCharts";
import { TableSkeleton } from "../ui/Skeleton";

export const CatalystFeedbackPanel: FC<{ eventId: string }> = ({ eventId }) => {
  const qc = useQueryClient();
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["catalyst", "feedback", eventId],
    queryFn: () => getCatalystEventFeedback(eventId),
  });
  const send = useMutation({
    mutationFn: () => sendCatalystFeedback(eventId),
    onSuccess: (res) => {
      setNotice({ ok: true, text: res.message });
      qc.invalidateQueries({ queryKey: ["catalyst", "feedback", eventId] });
      qc.invalidateQueries({ queryKey: ["catalyst", "attendees", eventId] });
    },
    onError: (err) =>
      setNotice({
        ok: false,
        text:
          (axios.isAxiosError(err) && (err.response?.data as { message?: string })?.message) ||
          "Couldn't send. Please try again.",
      }),
  });

  if (isLoading) return <TableSkeleton />;
  if (isError || !data) return <div className="card p-6 text-red-400 text-sm">Couldn&rsquo;t load feedback.</div>;

  const s = data.send;
  const reason = !s.eventStarted
    ? "You can send the form from the day of the meetup."
    : s.eligible === 0
      ? "The form only goes to people checked in at the door, and nobody has been checked in for this meetup yet."
      : s.toSend === 0
        ? s.recentlySent
          ? "Everyone got the form in the last 24 hours. You can resend to people who haven't answered after that."
          : "Every attendee has answered."
        : null;

  return (
    <div className="space-y-4">
      <div className="card p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold flex items-center gap-2">
              <MessageSquare size={15} className="text-brand-green" /> GEW feedback form
            </p>
            <p className="text-brand-muted text-xs mt-0.5">
              Each person gets an email with their own link to rate the meetup. It only goes to
              people checked in at the door, never to someone who already answered.
            </p>
          </div>
          <button
            disabled={!s.canSend || send.isPending}
            onClick={() => {
              setNotice(null);
              if (window.confirm(`Email the feedback form to ${s.toSend} attendee${s.toSend !== 1 ? "s" : ""}?`)) {
                send.mutate();
              }
            }}
            className="btn-primary text-sm shrink-0 inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            <Send size={14} />
            {send.isPending ? "Sending…" : s.toSend > 0 ? `Send feedback form (${s.toSend})` : "Send feedback form"}
          </button>
        </div>
        <p className="text-brand-muted text-xs">
          Checked in: {s.eligible} · emailed: {s.emailed} · answered: {s.submitted}
          {s.lastSentAt ? ` · last sent ${new Date(s.lastSentAt).toLocaleString()}` : ""}
        </p>
        {reason && <p className="text-brand-muted text-xs">{reason}</p>}
        {notice && (
          <p className={`text-sm rounded-lg px-3 py-2 border ${
            notice.ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-red-500/40 bg-red-500/10 text-red-400"
          }`} dir="auto">
            {notice.text}
          </p>
        )}
      </div>
      {data.summary.emailed > 0 && (
        <>
          <FeedbackKpis summary={data.summary} />
          <FeedbackCharts summary={data.summary} />
          <ResponsesList rows={data.responses} />
        </>
      )}
    </div>
  );
};
